<template lang="pug">
.fraud-case-container(@click="expand = !expand")
  .fraud-case-summary
    .fraud-case-code
      span.material-symbols-outlined phishing
      strong.fraud-case-number {{ data.code || ('FRAUD-' + data.number) }}

    .fraud-case-risk
      span.tag(:style="riskStyle") {{ riskLabel }}

    .fraud-case-line
      span.tag {{ triggerLineLabel }}

    .fraud-case-origin.order-number
      span.tag.fraud-search-link(:class="`bg-${data.brand}`" @click.stop="openOrderSearch(data.originOrder)") {{ data.originOrder }}

    .fraud-case-pending(v-if="data.status === 'active'")
      span.tag(:style="pendingStyle") {{ pendingLabel }}

    .fraud-case-date {{ dateName(data.createdAt) }} - {{ time(data.createdAt) }}

  transition(name="slide-fade")
    .fraud-case-details(v-if="expand" @click.stop="")
      .fraud-case-bulk-actions(
        v-if="data.status === 'active' && reviewableOrders.length && hasPermission"
        @click.stop=""
      )
        .fraud-case-select-all
          shCheckbox(:value="allReviewableSelected" @input="toggleSelectAll" icon-size="1.6rem")
          span Seleccionar todos ({{ reviewableOrders.length }})
        span.fraud-selected-count(v-if="selectedCount") {{ selectedCount }} seleccionada{{ selectedCount === 1 ? '' : 's' }}
        shActionButton.fraud-btn-dismiss-all(
          :action="reviewSelectedDismissed"
          text="Descartar seleccionados"
          textRunning="Descartando..."
          :textConfirmation="reviewSelectedDismissConfirmation"
          :disabled="!selectedCount || reviewAllLoading"
          width="13rem"
        )
        shActionButton.fraud-btn-fraud-all(
          :action="reviewSelectedFraud"
          text="Marcar fraude"
          textRunning="Marcando..."
          :textConfirmation="reviewSelectedFraudConfirmation"
          :disabled="!selectedCount || reviewAllLoading"
          warning
          width="11rem"
        )
      .fraud-orders-list
        .fraud-order-row.order-data-container(
          v-for="row in sortedOrders"
          :key="row.order"
          :class="rowRowClass(row)"
          @click.stop=""
        )
          .fraud-order-select(v-if="canReview(row)" @click.stop="")
            shCheckbox(:value="isOrderSelected(row.order)" @input="toggleSelectOrder(row.order, $event)" icon-size="1.6rem")
          .order-data.fraud-order-data
            .order-number
              span.tag.fraud-search-link(:class="`bg-${data.brand}`" @click.stop="openOrderSearch(row.order)") {{ row.order }}

            .divider-order

            .order-status
              .tag(:style="payStatusStyle(row)") {{ payStatusLabel(row) }}

            .divider-order

            .order-method-pay.fraud-order-pays(@click.stop="togglePayErrors(row)")
              .fraud-pay-block(v-for="(pay, idx) in payAttemptsList(row)" :key="idx")
                .fraud-pay-attempt
                  img.fraud-method-icon(v-if="payMethodIcon(pay)" :src="`${imagePath}/methods/light/mini-${payMethodIcon(pay)}.png`" :alt="payMethodIcon(pay)")
                  span.fraud-last-four(v-if="pay.lastFour") {{ pay.lastFour }}
                .fraud-pay-error(v-if="isPayErrorsExpanded(row.order)") {{ payErrorText(pay) }}
              span.fraud-pay-empty(v-if="!payAttemptsList(row).length") —

            .divider-order

            .order-total {{ money(row.total) }}

            .divider-order

            .order-name.capitalize
              span(v-for="(part, idx) in nameParts(row)" :key="idx")
                span(:class="{ 'fraud-highlight': part.highlight }") {{ part.text }}
                span(v-if="idx < nameParts(row).length - 1") {{ ' ' }}

            .divider-order

            .fraud-order-contact
              .fraud-contact-line.fraud-search-link(:class="{ 'fraud-highlight': row.matches && row.matches.phone }" @click.stop="openPhoneSearch(row.phone)") {{ row.phone || '—' }}
              .fraud-contact-line(:class="{ 'fraud-highlight': row.matches && row.matches.email }") {{ row.email || '—' }}
              .fraud-contact-line.capitalize(:class="{ 'fraud-highlight': row.matches && (row.matches.location || row.matches.community) }") {{ row.community || row.location || '—' }}
              .fraud-contact-line
                span(v-for="(part, idx) in addressParts(row)" :key="idx")
                  span(:class="{ 'fraud-highlight': part.highlight }") {{ part.text }}
                  span(v-if="idx < addressParts(row).length - 1") {{ ' ' }}
              .fraud-relation-related(v-if="relatedToSuffix(row)") {{ relatedToSuffix(row) }}
              .fraud-relation-ai(v-if="row.aiReason") IA: {{ row.aiReason }}
              .fraud-relation-ai(v-if="row.aiAssessment") Conclusión IA: {{ aiAssessmentLabel(row.aiAssessment) }}
              .fraud-relation-ai(v-if="row.aiEvidence && row.aiEvidence.length")
                ul.fraud-ai-evidence
                  li(v-for="(ev, eidx) in row.aiEvidence" :key="eidx") {{ ev }}
              .fraud-relation-ai(v-if="row.reportNumber") Informe REPORT-{{ row.reportNumber }}
              .fraud-relation-verified(v-if="row.verifiedAt") verificada

            .divider-order

            .fraud-order-date-block
              .order-date {{ dateName(row.createdAt) }} - {{ time(row.createdAt) }}
              span.fraud-dismissed-count(v-if="row.fraudDismissedCount") D: {{ row.fraudDismissedCount }}

            .fraud-order-count-col(v-if="row.fraudConfirmedCount")
              span.tag.order-fraud-count(:title="fraudCountTitle(row.fraudConfirmedCount)") {{ row.fraudConfirmedCount }}

          .order-actions-container.fraud-order-actions(@click.stop="")
            .order-actions
              .buttons-actions
                shActionButton.fraud-btn-dismiss(
                  v-if="data.status === 'active' && canReview(row)"
                  :action="reviewDismissed"
                  :params="[row.order]"
                  text="Descartar"
                  textRunning="Descartando"
                  :textConfirmation="dismissConfirmation(row.order)"
                  :disabled="reviewLoading === row.order || reviewAllLoading"
                  mini
                  width="9rem"
                )
                shActionButton(
                  v-if="data.status === 'active' && canReview(row)"
                  :action="reviewFraud"
                  :params="[row.order]"
                  text="Fraude"
                  textRunning="Marcando"
                  :textConfirmation="fraudConfirmation(row.order)"
                  :disabled="reviewLoading === row.order || reviewAllLoading"
                  mini
                  warning
                  width="9rem"
                )
                shActionButton.fraud-btn-ban(
                  v-if="canBanUser(row)"
                  :action="banUserOrder"
                  :params="[row.order]"
                  text="Banear"
                  textRunning="Baneando"
                  :textConfirmation="banConfirmation(row.order)"
                  :disabled="banLoading === row.order"
                  mini
                  width="9rem"
                )
</template>

<script>
import gql from '@my/graphql'
import gqlFraudCase from '@/graphql/fraudCase'
import gqlOrder from '@/graphql/order'
import mFilters from '@/mixins/filters'
import modals from '@my/store/modals'
import config from '@/config'
import shActionButton from '@/components/elements/ActionButton'
import shCheckbox from '@/components/elements/Checkbox'
import { fraudCountTitle } from '@/helpers/fraudReview'

export default {
  name: 'FraudCase',

  mixins: [mFilters],

  components: { shActionButton, shCheckbox },

  props: {
    data: Object,
    index: Number,
    isNew: Boolean,
    history: Boolean
  },

  data () {
    return {
      expand: false,
      reviewLoading: '',
      reviewAllLoading: false,
      banLoading: '',
      expandedPayRows: {},
      selectedOrders: {},
      imagePath: config.IMAGES.path,
      statusSpanish: {
        created: 'Creado',
        approved: 'Aprobado',
        paid: 'Pagado',
        packed: 'Empacado',
        sent: 'Enviado',
        declined: 'Declinado',
        expired: 'Expirado',
        delivered: 'Entregado'
      }
    }
  },

  computed: {
    primaryAlert () {
      if (!this.data.alerts || !this.data.alerts.length) return null
      const nonManual = this.data.alerts.filter(a => a.line !== 'manual')
      if (nonManual.length) return nonManual[nonManual.length - 1]
      return this.data.alerts[this.data.alerts.length - 1]
    },

    sortedOrders () {
      const orders = [...(this.data.orders || [])]
      return orders.sort((a, b) => {
        if (a.order === this.data.originOrder) return -1
        if (b.order === this.data.originOrder) return 1
        return 0
      })
    },

    pendingCount () {
      return (this.data.orders || []).filter(o => o.review && o.review.status === 'pending').length
    },

    reviewableOrders () {
      return (this.data.orders || []).filter(o => this.canReview(o))
    },

    selectedOrderIds () {
      return Object.keys(this.selectedOrders).filter(order => this.selectedOrders[order])
    },

    selectedCount () {
      return this.selectedOrderIds.length
    },

    allReviewableSelected () {
      const reviewable = this.reviewableOrders
      if (!reviewable.length) return false
      return reviewable.every(row => this.selectedOrders[row.order])
    },

    pendingLabel () {
      const count = this.pendingCount
      return `${count} pendiente${count === 1 ? '' : 's'}`
    },

    triggerLineLabel () {
      const line = (this.primaryAlert && this.primaryAlert.line) || this.data.triggerLine
      if (line === 'manual') return 'Verificación manual'
      if (line === 'ai') return 'Informe IA'
      if (line === 'A') return 'Línea A'
      if (line === 'B') return 'Línea B'
      if (line === 'C') return 'Línea C'
      return line ? `Línea ${line}` : ''
    },

    riskLabel () {
      const labels = {
        high: 'Riesgo alto',
        mediumHigh: 'Riesgo medio alto',
        medium: 'Riesgo medio'
      }
      return labels[this.data.riskLevel] || this.data.riskLevel
    },

    riskStyle () {
      const styles = {
        high: 'background:#d9534f;color:#fff',
        mediumHigh: 'background:#ff7043;color:#fff',
        medium: 'background:#ffb74d;color:#fff'
      }
      return styles[this.data.riskLevel] || 'background:#e9e9e7;color:#616161'
    },

    pendingStyle () {
      return this.pendingCount > 0
        ? 'background:#ffb74d;color:#fff'
        : 'background:#A5AA72;color:#fff'
    }
  },

  beforeDestroy () {
    this.expand = false
    this.selectedOrders = {}
  },

  methods: {
    fraudCountTitle,
    openOrderSearch (orderId) {
      if (!orderId) return
      const route = this.$router.resolve({
        path: '/orders',
        query: { Order: String(orderId) }
      })
      window.open(route.href, '_blank')
    },

    openPhoneSearch (phone) {
      const value = phone ? String(phone).trim() : ''
      if (!value || value === '—') return
      const route = this.$router.resolve({
        path: '/orders',
        query: { phones: value }
      })
      window.open(route.href, '_blank')
    },

    togglePayErrors (row) {
      if (!this.payAttemptsList(row).length) return
      const orderId = row.order
      this.$set(this.expandedPayRows, orderId, !this.expandedPayRows[orderId])
    },

    isPayErrorsExpanded (orderId) {
      return !!this.expandedPayRows[orderId]
    },

    payErrorText (pay) {
      if (!pay) return 'Sin error registrado.'
      const error = pay.error ? String(pay.error).trim() : ''
      return error || 'Sin error registrado.'
    },

    payAttemptsList (row) {
      if (row.pays && row.pays.length) {
        return row.pays.filter(p => p.lastFour || p.methodPay || p.cardType || p.error || p.state)
      }
      if (row.lastFour || row.methodPay || row.cardType) {
        return [{ methodPay: row.methodPay, cardType: row.cardType, lastFour: row.lastFour, state: row.payStatus, error: null }]
      }
      return []
    },

    payMethodIcon (pay) {
      return pay.methodPay || pay.cardType || null
    },

    payStatusLabel (row) {
      const status = row.payStatus || ''
      return this.statusSpanish[status] || status || '—'
    },

    payStatusStyle (row) {
      const status = row.payStatus || ''
      const style = this.colorsStatus[status]
      if (!style) return 'background:#e9e9e7;color:#616161'
      return `background:${style.background};color:${style.color}`
    },

    dismissConfirmation (orderId) {
      return `¿Descartar la orden ${orderId}? No es estafa.`
    },

    dismissAllConfirmation () {
      const code = this.data.code || `FRAUD-${this.data.number}`
      const count = this.pendingCount
      return `¿Descartar las ${count} orden${count === 1 ? '' : 'es'} pendiente${count === 1 ? '' : 's'} del caso ${code}? Solo este caso, no afecta otros.`
    },

    reviewSelectedDismissConfirmation () {
      const count = this.selectedCount
      return `¿Descartar ${count} orden${count === 1 ? '' : 'es'} seleccionada${count === 1 ? '' : 's'}? No es estafa.`
    },

    reviewSelectedFraudConfirmation () {
      const count = this.selectedCount
      return `¿Marcar ${count} orden${count === 1 ? '' : 'es'} seleccionada${count === 1 ? '' : 's'} como fraude?`
    },

    fraudConfirmation (orderId) {
      return `¿Marcar la orden ${orderId} como fraude?`
    },

    banConfirmation (orderId) {
      return `¿Banear teléfono, email y documento de la orden ${orderId}? (sin IP)`
    },

    reviewDismissed (orderId) {
      return this.reviewOrder(orderId, 'dismissed')
    },

    reviewSelectedDismissed () {
      return this.reviewSelected('dismissed')
    },

    reviewSelectedFraud () {
      return this.reviewSelected('fraud')
    },

    dismissAllPending () {
      return this.reviewAllPending()
    },

    reviewFraud (orderId) {
      return this.reviewOrder(orderId, 'fraud')
    },

    banUserOrder (orderId) {
      return this.banOrder(orderId)
    },

    nameParts (row) {
      const name = row.name || ''
      const highlights = (row.matches && row.matches.nameWords) || []
      const highlightSet = new Set(highlights.map(w => w.toLowerCase()))
      return name.split(' ').filter(Boolean).map(word => ({
        text: word,
        highlight: highlightSet.has(word.toLowerCase())
      }))
    },

    addressParts (row) {
      const address = row.address || ''
      if (!address) return [{ text: '—', highlight: false }]
      const highlights = ((row.matches && row.matches.addressSegments) || [])
        .map(part => String(part).toLowerCase())
      const highlightSet = new Set(highlights)
      const fullNorm = address.toLowerCase().trim()
      const fullMatch = highlightSet.has(fullNorm)
      return address.split(' ').filter(Boolean).map(word => ({
        text: word,
        highlight: fullMatch || highlightSet.has(word.toLowerCase())
      }))
    },

    relatedToSuffix (row) {
      if (!row.relatedTo || row.relatedTo === this.data.originOrder) return ''
      return ` · respecto a ${row.relatedTo}`
    },

    rowRowClass (row) {
      const classes = {}
      if (row.review && row.review.status === 'fraud') {
        classes['fraud-row-confirmed'] = true
      } else if (row.review && row.review.status === 'dismissed') {
        classes['fraud-row-dismissed'] = true
      } else if (row.order === this.data.originOrder) {
        classes['fraud-row-origin'] = true
      } else if (row.relationType === 'possible') {
        classes['fraud-row-possible'] = true
      }
      return classes
    },

    canBanUser (row) {
      if (row && row.bannedAt) return false
      return this.hasPermission()
    },

    canReview (row) {
      if (this.data.status !== 'active') return false
      if (!this.hasPermission()) return false
      return row.review && row.review.status === 'pending'
    },

    isOrderSelected (orderId) {
      return !!this.selectedOrders[orderId]
    },

    toggleSelectOrder (orderId, selected) {
      this.$set(this.selectedOrders, orderId, selected)
    },

    toggleSelectAll (selected) {
      this.reviewableOrders.forEach(row => {
        this.$set(this.selectedOrders, row.order, selected)
      })
    },

    clearSelectedOrders () {
      this.selectedOrders = {}
    },

    aiAssessmentLabel (assessment) {
      const labels = {
        fraud: 'Fraude',
        likely_fraud: 'Alta probabilidad de fraude',
        suspicious: 'Sospechosa'
      }
      return labels[assessment] || assessment
    },

    hasPermission () {
      try {
        const permissions = JSON.parse(this.$store.state.variables.permissionsString)
        const userPermissions = permissions[this.$store.state.user.type]
        return userPermissions &&
          userPermissions.permissions.fraudCase &&
          userPermissions.permissions.fraudCase.update
      } catch (error) {
        return false
      }
    },

    async reviewOrder (orderId, status) {
      if (status === 'pending') return
      this.reviewLoading = orderId
      const code = this.data.code || `FRAUD-${this.data.number}`
      try {
        const response = await gql.mutate(gqlFraudCase.REVIEW, { code, order: orderId, status })
        if (response && response.data && response.data.reviewFraudOrder) {
          Object.assign(this.data, response.data.reviewFraudOrder)
        }
      } catch (error) {
        console.log(error)
        alert(error.message || 'Error al revisar la orden')
      } finally {
        this.reviewLoading = ''
      }
    },

    async reviewSelected (status) {
      if (!this.selectedCount || this.reviewAllLoading) return
      this.reviewAllLoading = true
      const code = this.data.code || `FRAUD-${this.data.number}`
      const orders = [...this.selectedOrderIds]
      try {
        const response = await gql.mutate(gqlFraudCase.REVIEW_ORDERS, { code, orders, status })
        if (response && response.data && response.data.reviewFraudOrders) {
          Object.assign(this.data, response.data.reviewFraudOrders)
          this.clearSelectedOrders()
        }
      } catch (error) {
        console.log(error)
        alert(error.message || 'Error al revisar las órdenes seleccionadas')
      } finally {
        this.reviewAllLoading = false
      }
    },

    async reviewAllPending () {
      if (this.reviewAllLoading || !this.pendingCount) return
      this.reviewAllLoading = true
      const code = this.data.code || `FRAUD-${this.data.number}`
      try {
        const response = await gql.mutate(gqlFraudCase.DISMISS_ALL_PENDING, { code })
        if (response && response.data && response.data.dismissAllPendingFraudOrders) {
          Object.assign(this.data, response.data.dismissAllPendingFraudOrders)
        }
      } catch (error) {
        console.log(error)
        alert(error.message || 'Error al descartar las órdenes')
      } finally {
        this.reviewAllLoading = false
      }
    },

    async banOrder (orderId) {
      if (!orderId) return
      this.banLoading = orderId
      try {
        const response = await gql.mutate(gqlOrder.BAN_USER, { order: orderId })
        if (response) {
          const row = (this.data.orders || []).find(o => o.order === orderId)
          if (row) {
            row.bannedAt = new Date().toISOString()
          }
          modals.showMessage({ message: `Se baneó el usuario de la orden ${orderId} (sin IP)` })
        }
      } catch (error) {
        console.log(error)
        modals.showMessage({ message: (error && error.message) || 'Error al banear', type: 'error' })
      } finally {
        this.banLoading = ''
      }
    }
  }
}
</script>

<style lang="stylus">
@import '~@/stylus/product.styl'

.fraud-case-container
  display flex
  flex-direction column
  border-top 1px solid rgba(0, 0, 0, .2)
  padding 1.5rem 0

.fraud-case-summary
  display flex
  align-items center
  flex-wrap wrap
  gap 1em
  cursor pointer
  font-size 1.2rem

  .order-number
    display flex
    align-items center
    gap .5em

.fraud-case-code
  display flex
  align-items center
  gap .5em
  min-width 10em

  .material-symbols-outlined
    color #d9534f

  .fraud-case-number
    color #d9534f

.fraud-case-details
  margin-top 1.5rem
  width 100%

.fraud-case-bulk-actions
  display flex
  align-items center
  flex-wrap wrap
  gap 1rem
  margin-bottom 0.75rem
  padding-bottom 0.75rem
  border-bottom 1px dashed #e0e0e0

.fraud-case-select-all
  display flex
  align-items center
  gap .5rem
  font-size 1rem
  color #424242

.fraud-selected-count
  font-size 1rem
  color #616161

.fraud-btn-fraud-all.button-action-container .button-action
  background #d9534f
  border-color #d9534f

  .text-action
    color #fff

.fraud-relation-related
  font-size .85em
  color #757575

.fraud-relation-ai
  font-size .85em
  color #1565c0
  margin-top .25em

.fraud-relation-verified
  font-size .85em
  color #388e3c
  font-weight 600
  margin-top .25em

.fraud-orders-list
  display flex
  flex-direction column
  width 100%
  overflow-x auto
  font-size 1.2rem

.fraud-order-row.order-data-container
  display flex
  flex-direction row
  align-items flex-start
  flex-wrap nowrap
  gap 1em
  justify-content space-between
  border-top 1px solid rgba(0, 0, 0, .2)
  padding 1.5rem 0
  position relative
  cursor default
  width 100%

  .fraud-order-select
    display flex
    align-items center
    flex 0 0 2.5rem
    padding-top .2rem

  &:first-child
    border-top none

  &.fraud-row-origin
    background rgba(249, 115, 22, 0.09)

  &.fraud-row-possible
    background rgba(37, 99, 235, 0.07)

  &.fraud-row-confirmed
    background rgba(217, 83, 79, 0.12)

  &.fraud-row-dismissed
    background rgba(165, 170, 114, 0.14)

  .order-data
    display flex
    align-items center
    flex-wrap wrap
    text-align left
    column-gap 1em
    row-gap 1em
    padding 0 1rem
    flex 1 1 auto
    min-width 0
    cursor default
    font-size 1.2rem

  .divider-order
    display none

  .order-number
    display flex
    align-items center
    width 5.5em
    min-width 5.5em

  .order-fraud-count
    display inline-flex
    align-items center
    justify-content center
    background #d9534f
    color #fff
    white-space nowrap
    font-size inherit
    line-height 1
    padding .1em .25em
    min-width 0
    max-width 100%
    box-sizing border-box

  .fraud-order-date-block
    display flex
    flex-direction row
    align-items center
    flex-wrap wrap
    gap 1em

  .fraud-order-count-col
    display flex
    align-items center
    justify-content center
    flex 0 0 4em
    width 4em
    min-width 4em
    text-align center

  .order-date
    font-size 1rem
    white-space nowrap
    flex-shrink 0
    min-width 10em
    width 10em

  .fraud-dismissed-count
    font-size .75em
    color #757575

  .order-status
    width 6em

  .order-method-pay
    display flex
    justify-content flex-start
    align-items flex-start
    width 11em
    min-width 11em

  .fraud-order-pays.order-method-pay
    flex-direction column
    gap .5em
    cursor pointer

  .fraud-pay-block
    display flex
    flex-direction column
    gap .2em

  .fraud-pay-error
    font-size .78em
    color #616161
    line-height 1.35
    word-break break-word
    white-space normal
    padding-left .1em

  .order-total
    width 8em

  .order-name
    display flex
    align-items center
    flex-wrap wrap
    width 8em

  .fraud-order-contact
    display flex
    flex-direction column
    gap .3em
    width 15em
    min-width 15em
    font-size .95em
    line-height 1.35

  .fraud-contact-line
    word-break break-word

  .fraud-relation-related
    font-size .85em
    color #757575
    margin-top .2em

  .fraud-relation-verified
    font-size .85em
    color #388e3c
    font-weight 600

  .order-actions-container
    display flex
    align-items flex-start
    gap 1.5rem
    padding 0 1rem
    flex 0 0 auto

  .order-actions
    display flex
    align-items center
    gap 1em

  .buttons-actions
    display flex
    flex-direction column
    gap .5em
    align-items stretch

  .fraud-btn-dismiss.button-action-container .button-action
    background #A5AA72
    border-color #A5AA72

    .text-action
      color #fff

  .fraud-btn-verify.button-action-container .button-action
    background #3b82f6
    border-color #3b82f6

    .text-action
      color #fff

  .fraud-btn-ban.button-action-container .button-action
    background #f97316
    border-color #f97316

    .text-action
      color #fff

.fraud-highlight
  background rgba(217, 83, 79, 0.12)
  border-radius 3px
  padding .1em .2em
  display inline-block

.fraud-search-link
  cursor pointer

  &:hover
    opacity .85

.fraud-pay-attempt
  display flex
  align-items center
  gap .45em
  white-space nowrap

.fraud-method-icon
  height 1em
  display block

.fraud-last-four
  font-size .95em
  color #424242
  font-weight 600
  letter-spacing .05em
</style>



// WEBPACK FOOTER //
// src/components/pages/fraudCase/FraudCase.vue